import { useRef, useState } from "react";
import { assetsApi } from "@/api/assets";
import type { BrandFontFile } from "@/api/product-library";

/**
 * Upload das FONTES da marca por papel (título/corpo). Compartilhado entre a
 * identidade visual do produto (KbDetail) e o onboarding da marca (StepMiv).
 *
 * Por que existe: a arte renderizada via HTML/Satori tentava usar a fonte da
 * marca buscando no Google Fonts — fontes pagas/proprietárias não estão lá, e a
 * peça caía no fallback Bricolage/Inter ("tudo com a mesma cara"). Aqui o
 * usuário sobe o ARQUIVO da fonte (.ttf/.otf/.woff); o renderizador carrega os
 * bytes e a arte sai na tipografia REAL do cliente.
 *
 * Valor = lista de { assetId, family, role } (no máx. 1 por papel).
 */

const ROLES: Array<{ role: "headline" | "corpo"; label: string; hint: string }> = [
  { role: "headline", label: "Fonte de título", hint: "Usada nos headlines/números (peso forte)." },
  { role: "corpo", label: "Fonte de corpo", hint: "Usada no texto, labels e legendas." },
];

export function BrandFontUploader({
  companyId,
  value,
  onChange,
}: {
  companyId: string;
  value: BrandFontFile[];
  onChange: (next: BrandFontFile[]) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {ROLES.map((r) => (
        <FontSlot
          key={r.role}
          companyId={companyId}
          role={r.role}
          label={r.label}
          hint={r.hint}
          font={value.find((f) => f.role === r.role) ?? null}
          onSet={(next) => {
            const rest = value.filter((f) => f.role !== r.role);
            onChange(next ? [...rest, next] : rest);
          }}
        />
      ))}
    </div>
  );
}

function FontSlot({
  companyId,
  role,
  label,
  hint,
  font,
  onSet,
}: {
  companyId: string;
  role: "headline" | "corpo";
  label: string;
  hint: string;
  font: BrandFontFile | null;
  onSet: (next: BrandFontFile | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await assetsApi.uploadFont(companyId, file);
      onSet({ assetId: res.assetId, family: res.suggestedFamily || "Fonte da marca", role });
    } catch (err) {
      setError((err as Error).message || "Falha no upload da fonte");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      style={{
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        padding: 12,
        background: "var(--wf-paper)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wf-ink)" }}>{label}</div>
          <div style={{ fontSize: 11.5, color: "var(--wf-ink-3)", marginTop: 2 }}>{hint}</div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".ttf,.otf,.woff,font/ttf,font/otf,font/woff"
          style={{ display: "none" }}
          onChange={(e) => handleUpload(e.target.files)}
        />
        {!font && (
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            style={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {busy ? "Enviando…" : "Enviar fonte"}
          </button>
        )}
      </div>

      {font && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            value={font.family}
            onChange={(e) => onSet({ ...font, family: e.target.value.slice(0, 80) })}
            placeholder="Nome da fonte (ex: Arboria)"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              padding: "8px 10px",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 8,
              background: "var(--wf-paper)",
              color: "var(--wf-ink)",
              outline: "none",
            }}
          />
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            style={{ flexShrink: 0 }}
          >
            Trocar
          </button>
          <button
            type="button"
            onClick={() => onSet(null)}
            title="Remover fonte"
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid var(--wf-ink-5)",
              background: "transparent",
              color: "var(--wf-ink-3)",
              cursor: "pointer",
              fontSize: 15,
            }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <p style={{ fontSize: 11.5, color: "#e5484d", margin: 0, fontWeight: 500 }}>{error}</p>
      )}
    </div>
  );
}
